/**
 * An ePassport / eID chip (ICAO 9303) step, in a browser.
 *
 * A browser cannot read the chip: Web NFC speaks NDEF only, never the ISO-DEP
 * the chip needs. The trustsy native SDKs read it with their own screens
 * (`sdk/contract/native-flow.md`). Here the step says so and lets the applicant
 * continue without the chip — the flow decides what that costs (usually a
 * human review).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postCollectionFlowEvent } from './client';
import { DOCUMENT_CHIP_UNAVAILABLE_EVENT } from './documentChip';
import type { CollectionFlowSnapshot } from './types';

interface DocumentChipStepProps {
  token: string;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
  /** In place of the session — the console's flow preview. */
  skip?: () => Promise<void>;
}

export function DocumentChipStep({ token, onAdvance, skip }: DocumentChipStepProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const continueWithout = async () => {
    setBusy(true);
    setFailed(false);
    try {
      if (skip) await skip();
      else
        onAdvance(
          await postCollectionFlowEvent(token, DOCUMENT_CHIP_UNAVAILABLE_EVENT, { reason: 'no_native_reader' }),
        );
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1 className="step-title">{t('flow.documentChip.title')}</h1>
      <p className="step-text" data-testid="document-chip-unavailable">
        {t('flow.documentChip.noReader')}
      </p>
      {failed ? (
        <p className="alert" role="alert">
          {t('flow.documentChip.error.generic')}
        </p>
      ) : null}
      <div className="actions">
        <button type="button" className="btn" disabled={busy} onClick={() => void continueWithout()} data-track="continue_without_chip">
          {t('flow.documentChip.continueWithout')}
        </button>
      </div>
    </>
  );
}
